import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { detectLanguage, persistLanguage, SUPPORTED_LANGS } from './detector';

import koCommon from './locales/ko/common.json';
import koLayout from './locales/ko/layout.json';
import koOnboarding from './locales/ko/onboarding.json';
import koDashboard from './locales/ko/dashboard.json';
import koSongs from './locales/ko/songs.json';
import koSongDetail from './locales/ko/song-detail.json';
import koRecord from './locales/ko/record.json';
import koRecentHistory from './locales/ko/recent-history.json';
import koVolforce from './locales/ko/volforce.json';
import koTagAnalysis from './locales/ko/tag-analysis.json';
import koDifftable from './locales/ko/difftable.json';
import koBingo from './locales/ko/bingo.json';
import koPlaylist from './locales/ko/playlist.json';
import koRivals from './locales/ko/rivals.json';
import koDuel from './locales/ko/duel.json';
import koUserProfile from './locales/ko/user-profile.json';
import koSettings from './locales/ko/settings.json';
import koAdmin from './locales/ko/admin.json';
import koUtil from './locales/ko/util.json';
import koAuth from './locales/ko/auth.json';
import koAchievement from './locales/ko/achievement.json';
import koKonasuteTimeline from './locales/ko/konasute-timeline.json';
import koMonitor from './locales/ko/monitor.json';

import enCommon from './locales/en/common.json';
import enLayout from './locales/en/layout.json';
import enOnboarding from './locales/en/onboarding.json';
import enDashboard from './locales/en/dashboard.json';
import enSongs from './locales/en/songs.json';
import enSongDetail from './locales/en/song-detail.json';
import enRecord from './locales/en/record.json';
import enRecentHistory from './locales/en/recent-history.json';
import enVolforce from './locales/en/volforce.json';
import enTagAnalysis from './locales/en/tag-analysis.json';
import enDifftable from './locales/en/difftable.json';
import enBingo from './locales/en/bingo.json';
import enPlaylist from './locales/en/playlist.json';
import enRivals from './locales/en/rivals.json';
import enDuel from './locales/en/duel.json';
import enUserProfile from './locales/en/user-profile.json';
import enSettings from './locales/en/settings.json';
import enAdmin from './locales/en/admin.json';
import enUtil from './locales/en/util.json';
import enAuth from './locales/en/auth.json';
import enAchievement from './locales/en/achievement.json';
import enKonasuteTimeline from './locales/en/konasute-timeline.json';
import enMonitor from './locales/en/monitor.json';

import jaCommon from './locales/ja/common.json';
import jaLayout from './locales/ja/layout.json';
import jaOnboarding from './locales/ja/onboarding.json';
import jaDashboard from './locales/ja/dashboard.json';
import jaSongs from './locales/ja/songs.json';
import jaSongDetail from './locales/ja/song-detail.json';
import jaRecord from './locales/ja/record.json';
import jaRecentHistory from './locales/ja/recent-history.json';
import jaVolforce from './locales/ja/volforce.json';
import jaTagAnalysis from './locales/ja/tag-analysis.json';
import jaDifftable from './locales/ja/difftable.json';
import jaBingo from './locales/ja/bingo.json';
import jaPlaylist from './locales/ja/playlist.json';
import jaRivals from './locales/ja/rivals.json';
import jaDuel from './locales/ja/duel.json';
import jaUserProfile from './locales/ja/user-profile.json';
import jaSettings from './locales/ja/settings.json';
import jaAdmin from './locales/ja/admin.json';
import jaUtil from './locales/ja/util.json';
import jaAuth from './locales/ja/auth.json';
import jaAchievement from './locales/ja/achievement.json';
import jaKonasuteTimeline from './locales/ja/konasute-timeline.json';
import jaMonitor from './locales/ja/monitor.json';

const NAMESPACES = [
  'common', 'layout', 'onboarding',
  'dashboard', 'songs', 'song-detail',
  'record', 'recent-history', 'volforce', 'tag-analysis',
  'difftable', 'bingo', 'playlist',
  'rivals', 'duel', 'user-profile', 'settings', 'admin', 'util',
  'auth', 'achievement', 'konasute-timeline', 'monitor',
] as const;

if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      lng: detectLanguage(),
      fallbackLng: 'ko',
      supportedLngs: [...SUPPORTED_LANGS],
      defaultNS: 'common',
      ns: [...NAMESPACES],
      resources: {
        ko: {
          common: koCommon,
          layout: koLayout,
          onboarding: koOnboarding,
          dashboard: koDashboard,
          songs: koSongs,
          'song-detail': koSongDetail,
          record: koRecord,
          'recent-history': koRecentHistory,
          volforce: koVolforce,
          'tag-analysis': koTagAnalysis,
          difftable: koDifftable,
          bingo: koBingo,
          playlist: koPlaylist,
          rivals: koRivals,
          duel: koDuel,
          'user-profile': koUserProfile,
          settings: koSettings,
          admin: koAdmin,
          util: koUtil,
          auth: koAuth,
          achievement: koAchievement,
          'konasute-timeline': koKonasuteTimeline,
          monitor: koMonitor,
        },
        en: {
          common: enCommon,
          layout: enLayout,
          onboarding: enOnboarding,
          dashboard: enDashboard,
          songs: enSongs,
          'song-detail': enSongDetail,
          record: enRecord,
          'recent-history': enRecentHistory,
          volforce: enVolforce,
          'tag-analysis': enTagAnalysis,
          difftable: enDifftable,
          bingo: enBingo,
          playlist: enPlaylist,
          rivals: enRivals,
          duel: enDuel,
          'user-profile': enUserProfile,
          settings: enSettings,
          admin: enAdmin,
          util: enUtil,
          auth: enAuth,
          achievement: enAchievement,
          'konasute-timeline': enKonasuteTimeline,
          monitor: enMonitor,
        },
        ja: {
          common: jaCommon,
          layout: jaLayout,
          onboarding: jaOnboarding,
          dashboard: jaDashboard,
          songs: jaSongs,
          'song-detail': jaSongDetail,
          record: jaRecord,
          'recent-history': jaRecentHistory,
          volforce: jaVolforce,
          'tag-analysis': jaTagAnalysis,
          difftable: jaDifftable,
          bingo: jaBingo,
          playlist: jaPlaylist,
          rivals: jaRivals,
          duel: jaDuel,
          'user-profile': jaUserProfile,
          settings: jaSettings,
          admin: jaAdmin,
          util: jaUtil,
          auth: jaAuth,
          achievement: jaAchievement,
          'konasute-timeline': jaKonasuteTimeline,
          monitor: jaMonitor,
        },
      },
      returnEmptyString: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });

  persistLanguage(i18n.language);

  i18n.on('languageChanged', (lng) => {
    persistLanguage(lng);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lng;
    }
  });

  if (typeof document !== 'undefined') {
    document.documentElement.lang = i18n.language;
  }
}

export default i18n;
